import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAdminAuth, setAdminAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { LayoutDashboard, ClipboardList, History, UtensilsCrossed, FolderTree, LayoutGrid, Bell, Star, Users, UserCog, Settings, LogOut, Coffee, Menu, X } from "lucide-react";

// cap = capability key managers need for this section (owners see everything).
const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard", cap: "dashboard" },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList, tid: "nav-orders", cap: "orders" },
  { to: "/admin/history", label: "History", icon: History, tid: "nav-history", cap: "orders" },
  { to: "/admin/menu", label: "Menu", icon: UtensilsCrossed, tid: "nav-menu", cap: "menu" },
  { to: "/admin/categories", label: "Categories", icon: FolderTree, tid: "nav-categories", cap: "menu" },
  { to: "/admin/floor", label: "Floor", icon: LayoutGrid, tid: "nav-floor", cap: "floor" },
  { to: "/admin/waiter", label: "Waiter", icon: Bell, tid: "nav-waiter", cap: "waiter" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, tid: "nav-reviews", cap: "reviews" },
  { to: "/admin/customers", label: "Customers", icon: Users, tid: "nav-customers", cap: "customers" },
  { to: "/admin/settings", label: "Settings", icon: Settings, tid: "nav-settings", cap: "settings" },
  { to: "/admin/staff", label: "Staff", icon: UserCog, tid: "nav-staff", ownerOnly: true },
];

function Pill({ n, tone, testid }) {
  const tones = {
    red: "bg-[var(--qd-primary)] text-white",
    amber: "bg-amber-500 text-white",
    green: "bg-green-600 text-white",
    blue: "bg-blue-500 text-white",
  };
  const cls = n > 0 ? tones[tone] : "bg-black/10 text-gray-400";
  return <span data-testid={testid} className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${cls}`}>{n}</span>;
}

export default function AdminLayout() {
  const auth = useAdminAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({ received: 0, preparing: 0, ready: 0, fulfilled: 0, pending_waiter: 0 });
  const [restaurant, setRestaurant] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const prev = useRef(null);
  const audio = useRef(null);
  const flash = useRef(null);
  const baseTitle = useRef(typeof document !== "undefined" ? document.title : "QRDine");

  useEffect(() => { if (!auth) nav("/admin/login"); }, [auth, nav]);
  useEffect(() => { if (auth) api.get("/admin/restaurant").then((r) => setRestaurant(r.data)).catch(() => {}); }, [auth]);

  useEffect(() => {
    if (!auth) return;
    let stopped = false;

    // Prime the audio context on the first user interaction (browsers block audio otherwise).
    const unlock = () => {
      try {
        if (!audio.current) audio.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audio.current.state === "suspended") audio.current.resume();
      } catch (e) { /* no audio */ }
    };
    window.addEventListener("pointerdown", unlock, { once: true });

    const chime = () => {
      try {
        if (!audio.current) audio.current = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = audio.current;
        if (ctx.state === "suspended") ctx.resume();
        const t0 = ctx.currentTime;
        [880, 1175].forEach((f, i) => {
          const o = ctx.createOscillator(), g = ctx.createGain();
          o.type = "sine"; o.frequency.value = f;
          o.connect(g); g.connect(ctx.destination);
          const t = t0 + i * 0.16;
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
          o.start(t); o.stop(t + 0.17);
        });
      } catch (e) { /* no audio */ }
    };

    const stopFlash = () => {
      if (flash.current) { clearInterval(flash.current); flash.current = null; }
      document.title = baseTitle.current;
    };
    const startFlash = (label) => {
      stopFlash();
      let on = false;
      flash.current = setInterval(() => { document.title = on ? baseTitle.current : label; on = !on; }, 1200);
    };

    const poll = async () => {
      try {
        const { data } = await api.get("/admin/badge-counts");
        if (stopped) return;
        setCounts(data);
        if (prev.current) {
          const newOrder = data.received > prev.current.received;
          const newWaiter = data.pending_waiter > prev.current.pending_waiter;
          if (newOrder || newWaiter) {
            chime();
            if (document.hidden) startFlash(newWaiter ? "🔔 Waiter call — QRDine" : "🔔 New order — QRDine");
          }
        }
        prev.current = data;
      } catch (e) { /* ignore poll errors */ }
    };

    poll();
    const iv = setInterval(poll, 5000);
    const onVisible = () => { if (!document.hidden) stopFlash(); };
    window.addEventListener("focus", stopFlash);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(iv);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("focus", stopFlash);
      document.removeEventListener("visibilitychange", onVisible);
      stopFlash();
    };
  }, [auth]);

  // Managers only see sections they were granted; owners/super see everything.
  // (Computed pre-early-return; hooks below must run unconditionally.)
  const role = auth?.user?.role;
  const perms = auth?.user?.permissions || {};
  const visibleNav = NAV.filter((n) => {
    if (n.ownerOnly) return role === "owner" || role === "super_admin";
    if (role === "manager") return !!perms[n.cap];
    return true;
  });

  // A manager landing on a section they can't see gets bounced to their first allowed one.
  const loc = useLocation();
  useEffect(() => {
    if (role !== "manager" || visibleNav.length === 0) return;  // no perms → show empty state, don't bounce
    const item = NAV.find((n) => loc.pathname.startsWith(n.to));
    if (item && !visibleNav.includes(item)) nav(visibleNav[0].to, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, role]);

  if (!auth) return null;

  // A manager granted nothing shouldn't be silently kicked to the login screen.
  if (role === "manager" && visibleNav.length === 0) {
    return (
      <div className="min-h-screen qd-admin grid place-items-center p-8 text-center">
        <div className="max-w-sm">
          <h1 className="font-display text-2xl font-bold tracking-tight mb-1">No sections enabled</h1>
          <p className="text-sm text-gray-500 mb-6">Your account doesn't have access to any sections yet. Ask the restaurant owner to grant you permissions.</p>
          <button data-testid="noperm-signout" onClick={() => { setAdminAuth(null); nav("/admin/login"); }} className="text-sm font-semibold text-[var(--qd-primary)]">Sign out</button>
        </div>
      </div>
    );
  }

  const navBody = (onNavigate) => (
    <>
      {auth?.user?.role === "super_admin" && (
        <button data-testid="admin-back-to-platform" onClick={() => { onNavigate?.(); nav("/super"); }} className="mx-3 mt-3 text-xs font-semibold text-[var(--qd-primary)] inline-flex items-center gap-1">← Back to platform</button>
      )}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {visibleNav.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={n.tid} onClick={() => onNavigate?.()}
            className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[var(--qd-charcoal)] text-white" : "text-gray-600 hover:bg-gray-50"}`}>
            <n.icon className="w-4 h-4 shrink-0" aria-hidden />
            <span className="flex-1">{n.label}</span>
            {n.to === "/admin/orders" && (
              <span className="inline-flex items-center gap-1" title={`${counts.received} received · ${counts.preparing} preparing · ${counts.ready} ready · ${counts.fulfilled} to pay`}>
                <Pill n={counts.received} tone="red" testid="badge-received" />
                <Pill n={counts.preparing} tone="amber" testid="badge-preparing" />
                <Pill n={counts.ready} tone="green" testid="badge-ready" />
                <Pill n={counts.fulfilled} tone="blue" testid="badge-fulfilled" />
              </span>
            )}
            {n.to === "/admin/waiter" && counts.pending_waiter > 0 && (
              <Pill n={counts.pending_waiter} tone="red" testid="badge-waiter" />
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-gray-200">
        <button data-testid="admin-logout" onClick={() => { setAdminAuth(null); nav("/admin/login"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  const brand = (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg bg-[var(--qd-primary)] grid place-items-center"><Coffee className="w-4 h-4 text-white" /></div>
      <div>
        <div className="font-display font-bold text-sm">QRDine</div>
        <div className="text-[10px] uppercase tracking-widest text-gray-400">{restaurant?.name || "…"}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen qd-admin md:flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 bg-white border-r border-gray-200 h-screen sticky top-0 flex-col">
        <div className="p-5 border-b border-gray-200">{brand}</div>
        {navBody()}
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between">
        <button data-testid="admin-menu-btn" aria-label="Open menu" onClick={() => setMenuOpen(true)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-50 -ml-2">
          <Menu className="w-5 h-5" aria-hidden />
        </button>
        {brand}
        <span className="inline-flex items-center gap-1">
          <Pill n={counts.received} tone="red" testid="badge-received-m" />
          <Pill n={counts.fulfilled} tone="blue" testid="badge-fulfilled-m" />
        </span>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-white flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              {brand}
              <button data-testid="admin-menu-close" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-50">
                <X className="w-5 h-5" aria-hidden />
              </button>
            </div>
            {navBody(() => setMenuOpen(false))}
          </div>
        </div>
      )}

      <main className="flex-1 p-4 md:p-8 max-w-full overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
